import { PageHeader } from '@ksyos/design-system';
import { useNavigate, useParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { ValidationErrors } from '@frontend/utils/validation';
import { ManagerAuthorization } from '@common/enums';
import { deviceAccessoryTypeAddEditSchema } from '@common/validation/deviceAccessoryType';
import {
    ButtonContainer,
    CancelButton,
    DropdownSearch,
    FormContainer,
    FormGroup,
    FormHeader,
    FormLabel,
    InputContainer,
    InputGroup,
    InputText,
    Loading,
    SaveSubmit,
} from '@frontend/components/elements/common';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';
import type { IDeviceType } from './DeviceAccessoryTypesList';

interface IDeviceAccessoryType {
    deviceTypeId: string | null;
    id?: string;
    name: string | null;
}

export function DeviceAccessoryTypesAddEdit() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [deviceAccessoryType, setDeviceAccessoryType] = useState<IDeviceAccessoryType>({
        name: null,
        deviceTypeId: null,
    });
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);

    assertHasAuthorization(ManagerAuthorization.deviceAndAccessoryTypes);

    useEffect(() => {
        if (id) {
            load();
        }
    }, [id]);

    return (
        <PageTemplate width="medium">
            <PageHeader
                title={id ? translateKey('deviceAccessoryTypes.edit') : translateKey('deviceAccessoryTypes.new')}
            />
            <Loading
                item={deviceAccessoryType}
                loaded={(deviceAccessoryTypeItem) => (
                    <FormContainer
                        formContext={{
                            placeholderGroupName: 'common',
                            item: deviceAccessoryTypeItem,
                            errors: errors,
                            onChange: async (key, value) => {
                                const updatedDeviceAccessoryType = {
                                    ...deviceAccessoryType,
                                    [key]: value,
                                };

                                setDeviceAccessoryType(updatedDeviceAccessoryType);
                                validateOne(updatedDeviceAccessoryType, key);
                            },
                            onDoValidate: async (key) => true,
                        }}
                        onSubmit={async () => await save()}
                    >
                        <FormHeader>{translateKey('deviceAccessoryTypes.details')}</FormHeader>

                        <FormGroup hasErrors={errors.has('name')}>
                            <FormLabel isRequired>{translateKey('common.name')}</FormLabel>
                            <InputGroup>
                                <InputText
                                    fieldKey="name"
                                    placeholder={translateKey('common.name')}
                                    value={deviceAccessoryType.name || undefined}
                                />
                            </InputGroup>
                        </FormGroup>

                        <FormGroup hasErrors={errors.has('deviceTypeId')}>
                            <FormLabel isRequired>{translateKey('devices.type')}</FormLabel>
                            <InputGroup>
                                <InputContainer>
                                    <DropdownSearch
                                        clearable={false}
                                        fieldKey="deviceTypeId"
                                        loadOptions={() => loadDeviceTypes()}
                                        placeholder={translateKey('common.search') + '...'}
                                        value={deviceAccessoryType.deviceTypeId}
                                    />
                                </InputContainer>
                            </InputGroup>
                        </FormGroup>

                        <ButtonContainer
                            alignRight
                            buttons={[
                                <CancelButton
                                    disabled={isSaving}
                                    to="/deviceAccessoryTypes"
                                />,
                                <SaveSubmit disabled={isSaving} />,
                            ]}
                        />
                    </FormContainer>
                )}
            />
        </PageTemplate>
    );

    async function load() {
        const result = await fetchJson(`/api/deviceAccessoryTypes/${id}`, 'GET');
        const deviceAccessoryTypeResult = result.json;
        setDeviceAccessoryType(deviceAccessoryTypeResult);
    }

    function validateOne(accessoryType: IDeviceAccessoryType, name: keyof IDeviceAccessoryType) {
        setErrors(updateValidationErrorsForOne(deviceAccessoryTypeAddEditSchema, accessoryType, errors, name));
    }

    async function loadDeviceTypes() {
        const result = await fetchJson('/api/deviceTypes', 'GET');

        return result.json.items.map((item: IDeviceType) => ({
            value: item.id,
            label: item.name,
        }));
    }

    async function save() {
        const isSuccessful = await saveDeviceAccessoryType();
        if (isSuccessful) {
            navigate('/deviceAccessoryTypes');
        }
    }

    async function saveDeviceAccessoryType() {
        let url = '/api/deviceAccessoryTypes';
        if (id) {
            url += `/${id}`;
        }

        const { errors: validationErrors, item } = validate(deviceAccessoryTypeAddEditSchema, deviceAccessoryType);
        setErrors(validationErrors);

        if (validationErrors.size > 0) {
            return false;
        }

        setIsSaving(true);
        await fetchJson(url, 'POST', item);

        return true;
    }
}
